import { Config, DriveStep, driver } from 'driver.js';
import 'driver.js/dist/driver.css';

export const driverConfig: Config = {
  showProgress: true,
  showButtons: ['next', 'previous', 'close'],
  progressText: '{{current}} of {{total}}',
  nextBtnText: 'Next',
  prevBtnText: 'Previous',
  doneBtnText: 'Done',
  popoverClass: 'fuse-tour-popover',
  animate: true,
  overlayColor: 'rgba(0, 0, 0, 0.5)',
  smoothScroll: true,
  allowClose: true,
};

export const createTour = (steps: DriveStep[], config?: Partial<Config>) => {
  return driver({
    ...driverConfig,
    ...config,
    steps,
  });
};

export const dashboardTour: DriveStep[] = [
  {
    element: '[data-tour="dashboard-header"]',
    popover: {
      title: 'Welcome to FUSE Dashboard',
      description: 'This is your main dashboard where you can view key metrics and navigate to different sections.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-dashboard"]',
    popover: {
      title: 'Dashboard Navigation',
      description: 'Return to the dashboard at any time by clicking here.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-company"]',
    popover: {
      title: 'Company Section',
      description: 'Access your company details, facilities, documents, stripe details, and statements.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-facilities"]',
    popover: {
      title: 'Facilities Section',
      description: 'View and manage all facilities and their associated equipment.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-users"]',
    popover: {
      title: 'Users Section',
      description: 'Manage user accounts and permissions for your organization.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-statements"]',
    popover: {
      title: 'Statements Section',
      description: 'View financial statements and credit history.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="search-bar"]',
    popover: {
      title: 'Global Search',
      description: 'Quickly find clients, facilities, or equipment using the search bar.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '[data-tour="support-page-button"]',
    popover: {
      title: 'View Support Page',
      description: 'Access the FUSE support page for additional page help, FAQs, and troubleshooting questions.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '[data-tour="tour-reset-button"]',
    popover: {
      title: 'Restart Tour',
      description: 'Click here anytime to restart the tour for the current page if you need a refresher.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '[data-tour="user-settings"]',
    popover: {
      title: 'User Settings',
      description: 'Access profile details and securely sign out.',
      side: 'bottom',
      align: 'start',
    },
  },
];

export const facilitiesListTour: DriveStep[] = [
  {
    element: '[data-tour="facilities-list"]',
    popover: {
      title: 'Facilities Overview',
      description:
        'View all your facility locations here. Each facility can have multiple pieces of equipment enrolled in clean fuel programs.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="add-facility-button"]',
    popover: {
      title: 'Add New Facility',
      description: 'Click here to add a new facility location to your account.',
      side: 'left',
      align: 'start',
    },
  },
  {
    element: '[data-tour="facility-search"]',
    popover: {
      title: 'Search Facilities',
      description: 'Quickly find a specific facility by address.',
      side: 'bottom',
      align: 'start',
    },
  },
];

export const facilityDetailsTour: DriveStep[] = [
  {
    element: '[data-tour="facility-info"]',
    popover: {
      title: 'Facility Information',
      description: 'View and update facility details including name, address, and FSE utility.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="facility-equipment-list"]',
    popover: {
      title: 'Equipment at This Facility',
      description:
        'View all equipment registered at this facility. Click on any equipment to see details or update usage data.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: '[data-tour="equipment-actions-button"]',
    popover: {
      title: 'Equipment Actions',
      description:
        'Click this menu to access equipment management options including add, edit, retire, and delete equipment.',
      side: 'bottom',
      align: 'start',
    },
  },
];

export const equipmentListTour: DriveStep[] = [
  {
    element: '[data-tour="equipment-list"]',
    popover: {
      title: 'Equipment Overview',
      description:
        'View all equipment under your facility. Each facility can have multiple pieces of equipment enrolled in clean fuel programs.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="add-equipment-button"]',
    popover: {
      title: 'Add New Equipment',
      description: 'Click here to add a new piece of equipment to this facility.',
      side: 'left',
      align: 'start',
    },
  },
  {
    element: '[data-tour="equipment-search"]',
    popover: {
      title: 'Search Equipment',
      description: 'Quickly find specific equipment by serial or VIN number.',
      side: 'bottom',
      align: 'start',
    },
  },
];

export const equipmentDetailsTour: DriveStep[] = [
  {
    element: '[data-tour="equipment-info"]',
    popover: {
      title: 'Equipment Information',
      description: 'View equipment specifications including make, model, serial number, and registration details.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '#equipment-usage-data',
    popover: {
      title: 'Quarterly Usage Data',
      description:
        'Enter kWh consumption data for each reporting quarter. This data is used to calculate your clean fuel credits.',
      side: 'top',
      align: 'start',
    },
  },
];

export const usersTour: DriveStep[] = [
  {
    element: '[data-tour="users-list"]',
    popover: {
      title: 'User Management',
      description: "View and manage all users who have access to your organization's FUSE account.",
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="add-user-button"]',
    popover: {
      title: 'Add New User',
      description: 'Invite new team members to access FUSE. You can assign different permission levels.',
      side: 'left',
      align: 'start',
    },
  },
  {
    element: '[data-tour="user-search"]',
    popover: {
      title: 'Search Users',
      description: 'Quickly find specific users by name, email, or other criteria.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="user-filters"]',
    popover: {
      title: 'User Filters',
      description:
        'Filter users by role (Admin, User, Facility User) or status (Active, Inactive) to narrow down the list.',
      side: 'bottom',
      align: 'start',
    },
  },
];

export const companyTour: DriveStep[] = [
  {
    element: '[data-tour="company-info"]',
    popover: {
      title: 'Company Information',
      description:
        "View and update your organization's corporate information including legal name, FEIN/Business number, and status.",
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="contacts-tab"]',
    popover: {
      title: 'Contacts Tab',
      description: 'Click here to manage contact information for key personnel and administrators.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="documents-tab"]',
    popover: {
      title: 'Documents Tab',
      description: 'Click here to upload and manage important documents related to your company and programs.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="stripe-tab"]',
    popover: {
      title: 'Stripe Details Tab',
      description: 'Click here to manage banking and payment details for receiving incentive payments.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="corporate-info"]',
    popover: {
      title: 'Corporate Details',
      description: "View and update your organization's corporate address information",
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '#facility_list',
    popover: {
      title: 'Facilities Overview',
      description: 'View all facility locations associated with this company. Click to manage individual facilities.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="statements-list"]',
    popover: {
      title: 'Statements History',
      description: 'View quarterly incentive statements showing credits generated and incentives.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="users-list"]',
    popover: {
      title: 'User Management',
      description: 'Manage user accounts and permissions for accessing this company information.',
      side: 'bottom',
      align: 'start',
    },
  },
];

export const statementsTour: DriveStep[] = [
  {
    element: '[data-tour="statements-list"]',
    popover: {
      title: 'Incentive Statements',
      description: 'View all your quarterly incentive statements showing credits generated and incentives.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="statement-details"]',
    popover: {
      title: 'Statement Details',
      description: 'Click on a statement to view detailed breakdown of credits, rates, and payment information.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="individual-statement"]',
    popover: {
      title: 'Individual Statement',
      description:
        'Click to view the summary statement with breakdown of credits and incentives of enrolled programs with the option to download pdf.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="detailed-statement"]',
    popover: {
      title: 'Detailed Statement',
      description:
        'Click to view the detailed statement with breakdown of credits and incentives of enrolled programs with the option to download pdf.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="view-individual-programs"]',
    popover: {
      title: 'View Individual Programs',
      description: 'Click to expand and view statements for each individual program within this reporting period.',
      side: 'bottom',
      align: 'start',
    },
  },
];

export const startTour = (
  tourName:
    | 'dashboard'
    | 'facilities-list'
    | 'facility-details'
    | 'equipment-list'
    | 'equipment-details'
    | 'users'
    | 'company'
    | 'statements'
) => {
  let steps: DriveStep[] = [];

  switch (tourName) {
    case 'dashboard':
      steps = dashboardTour;
      break;
    case 'facilities-list':
      steps = facilitiesListTour;
      break;
    case 'facility-details':
      steps = facilityDetailsTour;
      break;
    case 'equipment-list':
      steps = equipmentListTour;
      break;
    case 'equipment-details':
      steps = equipmentDetailsTour;
      break;
    case 'users':
      steps = usersTour;
      break;
    case 'company':
      steps = companyTour;
      break;
    case 'statements':
      steps = statementsTour;
      break;
  }

  const tour = createTour(steps);
  tour.drive();
};

export const hasSeenTour = (tourName: string): boolean => {
  return localStorage.getItem(`tour_completed_${tourName}`) === 'true';
};

export const markTourAsSeen = (tourName: string): void => {
  localStorage.setItem(`tour_completed_${tourName}`, 'true');
};

export const resetTour = (tourName: string): void => {
  localStorage.removeItem(`tour_completed_${tourName}`);
};

export const resetTours = (): void => {
  const keys = Object.keys(localStorage).filter((key) => key.startsWith('tour_completed_'));
  keys.forEach((key) => localStorage.removeItem(key));
};

export const getTourNameFromPath = (pathname: string, clientId?: number): string | null => {
  if (pathname === '/') return 'dashboard-tour';
  if (pathname.includes('/facilities') && pathname.includes('/equipment') && !pathname.includes('/create'))
    return 'equipment-list-tour';
  if (pathname.includes('/equipment/') && !pathname.includes('/create')) return 'equipment-details-tour';
  if (pathname.includes(`/clients/${clientId}/facilities/`) && !pathname.includes('/create'))
    return 'facility-details-tour';
  if (pathname.includes('/facilities') && !pathname.includes('/create')) return 'facilities-list-tour';
  if (pathname.includes(`/clients/${clientId}/users`)) return 'users-tour';
  if (pathname.includes(`/clients/${clientId}/statements`)) return 'statements-tour';
  if (pathname.includes(`/clients/${clientId}`) && !pathname.includes('/facilities') && !pathname.includes('/users'))
    return 'company-tour';
  return null;
};
